import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PurchaseTask } from '@prody/shared';
import {
  Button, Code, EmptyState, IconCheck, IconClose, IconPlus, ListPane,
  MoneyText, PageHeader, Popover, RollingNumber, Segmented, Stamp, Toolbar,
  Workbench, toast,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import {
  useGeneratePurchaseTasks, usePurchaseTasks, useUpdatePurchaseTask,
} from '~/platform/data/queries';
import './purchasing.css';

/* ==================================================================
   THE BUYER'S SCREEN.

   Held in one hand, at 04:10, in a cold hall, quite possibly with a
   glove on. This is the screen that justifies the `market` density
   and the Hall theme existing at all, and it is designed against a
   different set of constraints from everything else in Prody:

     - one thumb, so actions are full-height buttons at the row's end
     - ordered by stand, not by product: the buyer walks the floor
       stand by stand and re-sorting by anything else costs them steps
     - "who is waiting for this" is on the row, because when the stall
       has 9 kg instead of 12 the buyer decides on the spot which
       customer gets short, and they need the names to do it
     - marking something unavailable is destructive downstream, so the
       count of affected orders is on the button itself
   ================================================================== */

type Lens = 'todo' | 'all';

export default function PurchasingPage() {
  const { t } = useTranslation(['purchasing', 'common']);
  const { business, format, can } = useBusiness();
  const tradingDay = format.tradingDay();
  const { data: tasks = [], isLoading } = usePurchaseTasks(business.id, tradingDay);
  const update = useUpdatePurchaseTask(business.id, tradingDay);
  const generate = useGeneratePurchaseTasks(business.id, tradingDay);
  const [lens, setLens] = useState<Lens>('todo');

  const canBuy = can('purchase', 'update');

  const buildList = () => {
    generate.mutate(undefined, {
      onSuccess: (next) => {
        const orderCount = new Set(
          next.flatMap((task) => task.demandBreakdown.map((d) => d.orderId)),
        ).size;
        toast.ok(t('generated', { count: orderCount }));
      },
      onError: () => toast.error(t('common:errors.saveFailed')),
    });
  };

  /* Grouped by stand. A buyer's route through Mercabarna is physical;
     the list has to match the walk or it is worse than paper. */
  const groups = useMemo(() => {
    const visible = lens === 'todo' ? tasks.filter((task) => task.status === 'todo') : tasks;
    const byStand = new Map<string, { label: string; stand: string | null; tasks: PurchaseTask[] }>();
    for (const task of visible) {
      const key = task.standCode ?? task.supplierName ?? '—';
      const group = byStand.get(key) ?? {
        label: task.supplierName ?? '—',
        stand: task.standCode,
        tasks: [],
      };
      group.tasks.push(task);
      byStand.set(key, group);
    }
    return [...byStand.values()].sort((a, b) => (a.stand ?? 'zz').localeCompare(b.stand ?? 'zz'));
  }, [tasks, lens]);

  const remaining = tasks.filter((task) => task.status === 'todo').length;

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={format.date(tradingDay, 'weekday')}
        actions={
          <Button
            variant="primary"
            icon={<IconPlus />}
            disabled={!canBuy}
            busy={generate.isPending}
            onClick={buildList}
          >
            {t('generate')}
          </Button>
        }
      />

      <Toolbar
        trailing={
          /* The drum rolls down as the buyer works the list — the
             floor's own countdown. */
          <RollingNumber
            className="num pd-buy__remaining"
            value={`${remaining} / ${tasks.length}`}
          />
        }
      >
        <Segmented<Lens>
          label={t('common:actions.filter')}
          value={lens}
          onChange={setLens}
          options={[
            { value: 'todo', label: t('toBuy') },
            { value: 'all', label: t('common:labels.all') },
          ]}
        />
      </Toolbar>

      <ListPane>
        {!isLoading && groups.length === 0 ? (
          <EmptyState title={t('empty')} body={t('emptyBody')} />
        ) : (
          <div className="pd-buy">
            {groups.map((group) => (
              <section key={group.stand ?? group.label} className="pd-buy__stand">
                <header className="pd-buy__stand-head">
                  {group.stand ? (
                    <span className="pd-buy__stand-code">{group.stand}</span>
                  ) : (
                    <Stamp tone="accent" size="sm">{t('runs')}</Stamp>
                  )}
                  <span className="pd-buy__stand-name">{group.label}</span>
                  <span className="num faint">{group.tasks.length}</span>
                </header>

                {group.tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    editable={canBuy}
                    onBought={() =>
                      update.mutate(
                        {
                          taskId: task.id,
                          patch: {
                            status: 'bought',
                            bought: task.required,
                            completedAt: new Date().toISOString(),
                          },
                        },
                        { onError: () => toast.error(t('common:errors.saveFailed')) },
                      )
                    }
                    onUnavailable={() =>
                      update.mutate(
                        {
                          taskId: task.id,
                          patch: { status: 'unavailable', completedAt: new Date().toISOString() },
                        },
                        {
                          onSuccess: () =>
                            toast.warn(task.productName, {
                              detail: t('unavailableWarning', {
                                count: task.demandBreakdown.length,
                              }),
                            }),
                          onError: () => toast.error(t('common:errors.saveFailed')),
                        },
                      )
                    }
                  />
                ))}
              </section>
            ))}
          </div>
        )}
      </ListPane>
    </Workbench>
  );
}

function TaskRow({
  task,
  editable,
  onBought,
  onUnavailable,
}: {
  task: PurchaseTask;
  editable: boolean;
  onBought: () => void;
  onUnavailable: () => void;
}) {
  const { t } = useTranslation(['purchasing', 'common']);
  const { format } = useBusiness();

  const done = task.status !== 'todo';
  const tone =
    task.status === 'bought' ? 'ok'
      : task.status === 'partial' ? 'warn'
        : task.status === 'unavailable' ? 'alert'
          : 'neutral';

  return (
    <article className="pd-buy__task" data-done={done || undefined} data-tone={tone}>
      <span className="pd-band" aria-hidden="true" style={{ background: 'var(--tone)' }} />

      <div className="pd-buy__task-body">
        <div className="pd-buy__task-top">
          <h3 className="pd-buy__task-name truncate">{task.productName}</h3>
          <Code>{task.sku}</Code>
        </div>

        <div className="pd-buy__figures">
          <span className="pd-buy__figure">
            <span className="stencil">{t('required')}</span>
            <span className="num pd-buy__need">{format.quantity(task.required)}</span>
          </span>
          {task.buffer ? (
            <span className="pd-buy__figure">
              <span className="stencil">+</span>
              <span className="num faint">{format.quantity(task.buffer)}</span>
            </span>
          ) : null}
          {task.bought ? (
            <span className="pd-buy__figure">
              <span className="stencil">{t('bought')}</span>
              <span className="num">{format.quantity(task.bought)}</span>
            </span>
          ) : null}
          {task.expectedCost ? (
            <span className="pd-buy__figure">
              <span className="stencil">{t('common:labels.cost')}</span>
              <MoneyText value={task.actualCost ?? task.expectedCost} previous={task.actualCost ? task.expectedCost : null} bare size="sm" />
            </span>
          ) : null}
        </div>

        {/* Who is waiting. Tapped open rather than always shown: the
            names matter at the moment of a shortage, not before. */}
        <Popover
          align="start"
          width={280}
          trigger={
            <button type="button" className="pd-buy__demand">
              {t('demandFrom')}
              <span className="num">{task.demandBreakdown.length}</span>
            </button>
          }
        >
          <ul className="pd-buy__demand-list">
            {task.demandBreakdown.map((d) => (
              <li key={d.orderId}>
                <Code>{d.orderNumber}</Code>
                <span className="truncate">{d.customerName}</span>
                <span className="num">{format.quantity(d.quantity)}</span>
              </li>
            ))}
          </ul>
        </Popover>

        {task.note ? <p className="pd-buy__task-note">{task.note}</p> : null}
      </div>

      {editable && !done ? (
        <div className="pd-buy__task-actions">
          <button type="button" className="pd-buy__action" data-kind="ok" onClick={onBought}>
            <IconCheck />
            <span>{t('markBought')}</span>
          </button>
          <button type="button" className="pd-buy__action" data-kind="alert" onClick={onUnavailable}>
            <IconClose />
            <span>{t('markUnavailable')}</span>
          </button>
        </div>
      ) : (
        <div className="pd-buy__task-status">
          <Stamp tone={tone} weight="solid">
            {task.status === 'bought'
              ? t('bought')
              : task.status === 'unavailable'
                ? t('markUnavailable')
                : task.status}
          </Stamp>
        </div>
      )}
    </article>
  );
}
